import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {tint} from 'theme/helper';
import {useFormikContext} from 'formik';
import {Drawer, DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {DrawerType} from 'components/customer/Product/Drawer/DrawerType';
import {DrawerRunner} from 'components/customer/Product/Drawer/DrawerRunner';
import {DrawerFaceHeight} from 'components/customer/Product/Drawer/DrawerFaceHeight';
import {DrawerThumbnail} from 'components/customer/Product/Drawer/DrawerThumbnail';
import {Icon} from 'shared/helpers';
import {
    cabinetDefault,
    followsCabinet,
    shortRunner,
} from 'components/customer/Product/Drawer/helpers';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {
    Badge,
    Heading,
    Panel,
    ResetButton,
} from 'components/customer/Product/Drawer/layoutStyles';

interface SelectedDrawerProps {
    drawerParams: DrawerParameters;
    drawerAmount: number;
    selectedIndex: number;
}

// every drawer stays mounted so its runner lookup keeps running; only the
// selected one is shown
export const SelectedDrawer = ({
    drawerParams,
    drawerAmount,
    selectedIndex,
}: SelectedDrawerProps) => {
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const {defaults} = useDrawerDefaults();
    const compact = useCompactLayout();

    // what a drawer is judged against to know if it is overriding
    const cabinet = useMemo(() => {
        const drawers = values.drawers ?? [];

        return {
            type: cabinetDefault(
                drawers,
                drawerParams.drawerTypeFieldName,
                defaults.type
            ),
            runner: cabinetDefault(
                drawers,
                drawerParams.drawerRunnerFieldName,
                defaults.runner
            ),
        };
    }, [
        values.drawers,
        defaults,
        drawerParams.drawerTypeFieldName,
        drawerParams.drawerRunnerFieldName,
    ]);

    const hasDefault = cabinet.type !== '' || cabinet.runner !== '';

    const usesDefaults = useCallback(
        (drawer: Drawer) =>
            followsCabinet(
                drawer,
                cabinet,
                drawerParams.drawerTypeFieldName,
                drawerParams.drawerRunnerFieldName
            ),
        [
            cabinet,
            drawerParams.drawerTypeFieldName,
            drawerParams.drawerRunnerFieldName,
        ]
    );

    const autoHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const index = Number(event.currentTarget.dataset.index);

            void setTouched({drawers: true});
            void setFieldValue(
                `drawers[${index}].${drawerParams.drawerStyleFieldName}`,
                1
            );
        },
        [setFieldValue, setTouched, drawerParams.drawerStyleFieldName]
    );

    return (
        <Panel data-cy="selected-drawer">
            {Array.from(Array(drawerAmount).keys()).map((index) => {
                const drawer = values.drawers?.[Number(index)];
                const isAuto =
                    drawer?.[
                        drawerParams.drawerStyleFieldName as keyof typeof drawer
                    ] == 1;

                return (
                    <Block
                        key={index}
                        hidden={index !== selectedIndex}
                        data-cy={`selected-drawer-${index}`}>
                        <Heading data-cy={`drawer-heading-${index}`}>
                            <Badge>{index + 1}</Badge>
                            <span>Drawer {index + 1}</span>
                        </Heading>

                        <DrawerFaceHeight
                            runnerIndex={index}
                            fieldName={drawerParams.drawerFaceHeightFieldName}
                            drawerStyleFieldName={
                                drawerParams.drawerStyleFieldName
                            }
                            setsFixedOnChange
                            trailing={
                                <ResetButton
                                    variant="link"
                                    className="button-blue btn-sm"
                                    data-index={index}
                                    data-cy={`drawer-auto-${index}`}
                                    onClick={autoHandler}
                                    disabled={isAuto}>
                                    <Icon
                                        iconName="Reverse-Refresh.svg"
                                        color="white"
                                    />
                                    <span>Auto</span>
                                </ResetButton>
                            }
                        />

                        <HardwareHeading>Selected Hardware</HardwareHeading>

                        <HardwareRow>
                            <ThumbnailColumn $compact={compact}>
                                <Thumbnail
                                    $compact={compact}
                                    data-cy={`drawer-thumbnail-${index}`}>
                                    <DrawerThumbnail
                                        runnerIndex={index}
                                        drawerTypeFieldName={
                                            drawerParams.drawerTypeFieldName
                                        }
                                        drawerFaceHeightFieldName={
                                            drawerParams.drawerFaceHeightFieldName ||
                                            'drawer_face_height'
                                        }
                                        fingerPullFieldName={
                                            drawerParams.fingerPullFieldName
                                        }
                                        drawerDepth={
                                            drawerParams.maxRunnerDepth
                                        }
                                        className="runner-image"
                                        fallback={
                                            <Placeholder>
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="30"
                                                    height="30"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <rect
                                                        x="2"
                                                        y="8"
                                                        width="20"
                                                        height="8"
                                                        rx="1.5"
                                                    />
                                                    <path d="M2 12h20M6 16v2M18 16v2" />
                                                </svg>
                                                <span>
                                                    {shortRunner(
                                                        drawer?.[
                                                            drawerParams.drawerRunnerFieldName as keyof typeof drawer
                                                        ]
                                                    )}
                                                </span>
                                            </Placeholder>
                                        }
                                    />
                                </Thumbnail>
                            </ThumbnailColumn>

                            <HardwareFields
                                data-cy={`drawer-hardware-fields-${index}`}>
                                <DrawerType
                                    runnerIndex={index}
                                    fieldName={drawerParams.drawerTypeFieldName}
                                    isInnerDrawer={drawerParams.isInnerDrawer}
                                />
                                <DrawerRunner
                                    isInnerDrawer={drawerParams.isInnerDrawer}
                                    runnerIndex={index}
                                    fieldName={
                                        drawerParams.drawerRunnerFieldName
                                    }
                                    drawerTypeFieldName={
                                        drawerParams.drawerTypeFieldName
                                    }
                                    drawerFaceHeightFieldName={
                                        drawerParams.drawerFaceHeightFieldName ||
                                        'drawer_face_height'
                                    }
                                    fingerPullFieldName={
                                        drawerParams.fingerPullFieldName
                                    }
                                    drawerDepth={drawerParams.maxRunnerDepth}
                                />
                            </HardwareFields>
                        </HardwareRow>

                        {hasDefault ? (
                            <DefaultsHint
                                data-cy={`drawer-defaults-hint-${index}`}>
                                {usesDefaults(drawer)
                                    ? 'Using the cabinet defaults.'
                                    : 'Overrides the cabinet default for this drawer.'}
                            </DefaultsHint>
                        ) : null}
                    </Block>
                );
            })}
        </Panel>
    );
};

// square, matched to the two stacked fields beside it: label plus control,
// twice, plus the gap between them
const HARDWARE_IMAGE = 148;
// folded, the dropdowns need the room more than the picture
const COMPACT_IMAGE = 110;
const imageSize = (compact: boolean) =>
    compact ? COMPACT_IMAGE : HARDWARE_IMAGE;
// the label carries its own 7px of padding, so the box has to drop by that much
// to sit level with the text rather than with the label's box
const LABEL_PADDING = 7;

const ThumbnailColumn = styled.div<{$compact: boolean}>`
    flex: 0 0 ${({$compact}) => imageSize($compact)}px;
    width: ${({$compact}) => imageSize($compact)}px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const DefaultsHint = styled.div`
    margin-top: 8px;
    color: #6b7480;
    font-size: 12px;
    text-wrap: pretty;
`;

const HardwareHeading = styled.div`
    margin-top: 12px;
    font-weight: 700;
    color: ${({theme}) => theme.colors.primary?.main};
`;

const HardwareRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-top: 6px;
`;

const HardwareFields = styled.div`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    > div {
        margin-bottom: 0;
    }
`;

// stands in when the runner has no picture: the same box, a runner glyph and
// the code, so the group keeps its shape either way
const Placeholder = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px;
    text-align: center;
    box-sizing: border-box;
    color: ${({theme}) => tint(theme.colors.primary?.main, 0.45)};
    border: 1px solid ${({theme}) => tint(theme.colors.primary?.main, 0.18)};
    border-radius: 6px;
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.05)};

    span {
        color: #8b97a6;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.3px;
        font-variant-numeric: tabular-nums;
    }
`;

// its own class, so product.scss's 205px cap does not apply
const Thumbnail = styled.div<{$compact: boolean}>`
    flex: 0 0 ${({$compact}) => imageSize($compact)}px;
    height: ${({$compact}) => imageSize($compact)}px;
    margin-top: ${LABEL_PADDING}px;

    .runner-image {
        display: block;
        height: 100%;
        border: 1px solid ${({theme}) => tint(theme.colors.primary?.main, 0.18)};
        border-radius: 6px;
        overflow: hidden;
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
        /* fills the square; a photo that is not square is cropped rather than
           letterboxed */
        object-fit: cover;
    }
`;

const Block = styled.div`
    padding: 13px;

    &[hidden] {
        display: none;
    }

    /* FormGroup indents 10px, pushing fields out of line with the heading */
    > div:not(${Heading}) {
        padding-left: 0;
    }

    .row {
        margin-left: 0;
        margin-right: 0;
    }

    /* stack each field, label above the control, like the margins card */
    [class*='col-'] {
        flex: 0 0 100%;
        width: 100%;
        max-width: 100%;
        padding-left: 0;
        padding-right: 0;
    }
`;
